import { createContext, useContext, useEffect, useState } from 'react';
import { authServiceV2 } from '../../services/auth.service';
import type { NormalizedUser } from '../../types/user-schema';
import type { UserRole } from '../../types/roles';
import type { AuthSession } from '../../types/auth';
import { toast } from 'react-toastify';

// Create a directory for our context
interface AuthContextV2Type {
  user: NormalizedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, role?: UserRole) => Promise<AuthSession>;
  register: (data: any, role?: UserRole) => Promise<AuthSession>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<NormalizedUser>) => Promise<NormalizedUser>;
}

export const AuthContextV2 = createContext<AuthContextV2Type | undefined>(undefined);

/**
 * Hook to use the auth context
 */
export const useAuthV2 = () => {
  const context = useContext(AuthContextV2);
  if (!context) {
    throw new Error('useAuthV2 must be used within an AuthProviderV2');
  }
  return context;
};

/**
 * Provider component for the auth context
 */
export const AuthProviderV2: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<NormalizedUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        // Check for existing session
        const session = await authServiceV2.getSession();
        
        if (session?.user) {
          setUser(session.user as NormalizedUser);
          setIsAuthenticated(true);
          console.log('User authenticated:', session.user);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          console.log('No authenticated user');
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Login with email and password
   */
  const login = async (email: string, password: string, role: UserRole = 'business'): Promise<AuthSession> => {
    try {
      setIsLoading(true);
      
      // Use role-based login
      const session = await authServiceV2.loginWithRole(email, password, role);
      
      // Update state with user data
      setUser(session.user as NormalizedUser);
      setIsAuthenticated(true);
      
      toast.success('Login successful');
      return session;
    } catch (error: any) {
      toast.error(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register a new user
   */
  const register = async (data: any, role: UserRole = 'business'): Promise<AuthSession> => {
    try {
      setIsLoading(true);
      
      // Add role to registration data
      const userData = { ...data, role };
      
      // Register user
      const session = await authServiceV2.register(userData);
      
      // Update state with user data
      setUser(session.user as NormalizedUser);
      setIsAuthenticated(true);
      
      toast.success('Registration successful');
      return session;
    } catch (error: any) {
      toast.error(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout the current user
   */
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Logout user
      await authServiceV2.logout();
      
      // Update state
      setUser(null);
      setIsAuthenticated(false);
      
      toast.info('You have been logged out');
    } catch (error: any) {
      toast.error(error.message || 'Logout failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update user data
   */
  const updateUser = async (userData: Partial<NormalizedUser>): Promise<NormalizedUser> => {
    try {
      setIsLoading(true);
      
      // For now, just update the local state
      // In a real implementation, this would call an API to update the user data
      const updatedUser = { ...user, ...userData } as NormalizedUser;
      setUser(updatedUser);
      
      return updatedUser;
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user data');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Provide the auth context to children
  return (
    <AuthContextV2.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        register,
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContextV2.Provider>
  );
};